// src/pages/main-streaming-dashboard/index.tsx
import React, { useEffect, useState } from 'react';
import { Layout, message } from 'antd';
import Header from '../../components/Header';
import HostVideoStream from './components/HostVideoStream';
import BroadcastControls, { BroadcastState } from './components/BroadcastControls';
import RaiseHandsPanel from './components/RaiseHandsPanel';
import StreamQualityIndicator from './components/StreamQualityIndicator';
import ParticipantCounter from './components/ParticipantCounter';
import StreamManagementGrid from '../stream-management-grid';
import Icon from '../../components/AppIcon';
import { Button } from '../../components/AntComponents';
import AudienceSelectorDrawer from './components/AudienceSelectDrawer';
import { assignHostAndCoHost, mediamtxService } from '@/mediaservices/mediamtxService';
import { mediasoupService } from '@/mediaservices/mediasoupService';
import { MediaSoupBroadcast, MediaMTXUpdateBroadcast, MediaMTXGetStatus } from '@/api/broadcastService';
import { BroadcastStorage } from '@/utils/BroadcastStorage';

const { Content, Sider } = Layout;

interface Stream {
  id: string;
  participantName: string;
  deviceName: string; //device_serialNumber
  device_name?: string; // device_name 
  deviceType: string;
  isHost: boolean;
  isAudioOn: boolean;
  isVideoOn: boolean;
  connectionStrength: string;
  /** MediaMTX: WHEP URL for playback */
  whepUrl?: string | null;
  /** MediaSoup: MediaStream for playback */
  mediaStream?: MediaStream | null;
  /** ✅ ADD: Mute state from WebSocket */
  muteState?: { selfMuted: boolean; adminMuted: boolean };
}

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === 'true';

const MainStreamingDashboard: React.FC = () => {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [availableStreams, setAvailableStreams] = useState<Map<string, any>>(new Map());
  const [isHandsPanelCollapsed, setIsHandsPanelCollapsed] = useState(true);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(true);

  // ✅ Initialize from MediaMTX status for MediaMTX mode, localStorage for MediaSoup
  const [broadcastState, setBroadcastState] = useState<BroadcastState>('stopped');
  const [broadcastStartTime, setBroadcastStartTime] = useState<number | null>(null);
  const [broadcastName, setBroadcastName] = useState<string>('');

  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceKeys, setAudienceKeys] = useState<string[]>([]);
  const [raisedHandsCount, setRaisedHandsCount] = useState(0);
  const [duration, setDuration] = useState('00:00:00');

  // ✅ MediaMTX Status Fetching
  const fetchMediaMTXStatus = async () => {
    if (!USE_MEDIAMTX) return;
    
    try {
      const status = await MediaMTXGetStatus();

      if (status.success && status.broadcastStatus === 'ACTIVE') {
        // Broadcast is active
        setBroadcastState('live');
        setBroadcastName(status.broadcastName || 'Live Broadcast');
        
        // Calculate duration from start time
        if (status.broadcastStartTime) {
          const startTime = new Date(status.broadcastStartTime).getTime();
          setBroadcastStartTime(startTime);
          
          // Update duration immediately
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          const hours = Math.floor(elapsed / 3600);
          const minutes = Math.floor((elapsed % 3600) / 60);
          const seconds = elapsed % 60;
          setDuration(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
        }

        // Update audience keys from broadcast devices
        if (status.broadcastDeviceSerials) {
          // Convert device serials to audience keys format
          const keys = status.broadcastDeviceSerials.map(serial => `device-${serial}`);
          setAudienceKeys(keys);
          
          // Also update BroadcastStorage for consistency
          BroadcastStorage.storeSelectedDevices(status.broadcastDeviceSerials);
          BroadcastStorage.storeBroadcastName(status.broadcastName || 'Live Broadcast');
          BroadcastStorage.storeBroadcastState('live');
          if (status.broadcastId) {
            BroadcastStorage.storeStreamId(parseInt(status.broadcastId));
          }
        }

      } else {
        // No active broadcast
        setBroadcastState('stopped');
        setBroadcastName('');
        setBroadcastStartTime(null);
        setDuration('00:00:00');
        setAudienceKeys([]);
        
        // Clear storage
        BroadcastStorage.storeBroadcastState('stopped');
      }

    } catch (error) {
      // On error, fall back to localStorage for MediaMTX mode too
      const storedState = BroadcastStorage.getBroadcastState();
      if (storedState !== 'stopped') {
        setBroadcastState(storedState);
        setBroadcastName(BroadcastStorage.getBroadcastName());
        setBroadcastStartTime(BroadcastStorage.getBroadcastStartTime());
      }
    }
  };

  // ✅ Initialize state on component mount
  useEffect(() => {
    if (USE_MEDIAMTX) {
      // For MediaMTX: Always fetch status from API
      fetchMediaMTXStatus();
    } else {
      // For MediaSoup: Use localStorage as before
      setBroadcastState(BroadcastStorage.isBroadcastActive() ? BroadcastStorage.getBroadcastState() : 'stopped');
      setBroadcastStartTime(BroadcastStorage.getBroadcastStartTime());
      setBroadcastName(BroadcastStorage.getBroadcastName());
    }
  }, []);

  // ✅ Poll MediaMTX status every 30 seconds when active
  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    const interval = setInterval(() => {
      fetchMediaMTXStatus();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Duration timer (live only) - now works with MediaMTX start time
  useEffect(() => {
    let t: any;
    if (broadcastState === 'live') {
      t = setInterval(() => {
        if (USE_MEDIAMTX && broadcastStartTime) {
          // Calculate from actual start time for MediaMTX
          const elapsed = Math.floor((Date.now() - broadcastStartTime) / 1000);
          const hours = Math.floor(elapsed / 3600);
          const minutes = Math.floor((elapsed % 3600) / 60);
          const seconds = elapsed % 60;
          setDuration(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
        } else {
          // Incremental timer for MediaSoup
          setDuration(prev => {
            const [h, m, s] = prev.split(':').map(Number);
            const total = h * 3600 + m * 60 + s + 1;
            const hh = String(Math.floor(total / 3600)).padStart(2, '0');
            const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
            const ss = String(total % 60).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
          });
        }
      }, 1000);
    }
    return () => clearInterval(t);
  }, [broadcastState, broadcastStartTime]);

  // Build host/co-host tiles from service snapshot (unchanged)
  useEffect(() => {
    const service = USE_MEDIAMTX ? mediamtxService : mediasoupService;

    const rebuild = () => {
      const { hostId, coHostId } = service.getCurrentRoles();
      const list = service.getStreamSummaries();

      const find = (id?: string | null) => {
        const found = list.find(s => s.id === id);
        return found;
      };
      
      const hostStream = hostId ? find(hostId) : null;
      const coHostStream = coHostId ? find(coHostId) : null;


      const make = (s: any, isHost: boolean): Stream => {
        const deviceId = s?.meta?.deviceSerialNumber || s?.id || '';
        const muteState = muteStates.get(deviceId) || { selfMuted: false, adminMuted: false };
        
        return {
          id: s?.id ?? '',
          participantName: s?.meta?.participantName || s?.meta?.deviceSerialNumber || s?.id || '—',
          deviceName: s?.meta?.deviceName || s?.meta?.deviceSerialNumber || s?.id || '—',
          device_name: s?.meta?.device_name || '—',
          deviceType: s?.meta?.deviceType || 'mobile',
          isHost,
          isAudioOn: true,
          isVideoOn: USE_MEDIAMTX ? !!s?.whepUrl : !!s?.mediaStream,
          connectionStrength: isHost ? 'excellent' : 'good',
          muteState, // ✅ Include mute state
          ...(USE_MEDIAMTX 
            ? { whepUrl: s?.whepUrl || null }
            : { mediaStream: s?.mediaStream || null }
          )
        };
      };

      const next: Stream[] = [];
      
      // ✅ FIXED: Strict assignment - only add if found and role matches
      if (hostStream) {
        next.push(make(hostStream, true));
      } else {
        console.log('NO HOST stream to add');
      }
      
      if (coHostStream && coHostId !== hostId) {
        next.push(make(coHostStream, false));
      } else {
        console.log('NO CO-HOST stream to add');
      }
      setStreams(next);
    };

    const onUpdate = () => {
      rebuild();
    };
    const onRemoved = () => {
      rebuild();
    };
    const onRoles = () => {
      rebuild();
    };

    service.on('streamUpdate', onUpdate);
    service.on('streamRemoved', onRemoved);
    service.on('rolesChanged', onRoles);

    rebuild();

    return () => {
      service.off('streamUpdate', onUpdate);
      service.off('streamRemoved', onRemoved);
      service.off('rolesChanged', onRoles);
    };
  }, [USE_MEDIAMTX]);

  // Video update handler (unchanged)
  useEffect(() => {
    if (USE_MEDIAMTX) {
      const onVideoUpdate = (data: { id: string; videoPaused: boolean; success?: boolean }) => {
        if (data.success !== false) {
          setStreams(prevStreams => 
            prevStreams.map(stream => {
              if (stream.deviceName === data.id || stream.id === data.id) {
                return {
                  ...stream,
                  isVideoOn: !data.videoPaused
                };
              }
              return stream;
            })
          );
        }
      };

      mediamtxService.on('video', onVideoUpdate);
      return () => mediamtxService.off('video', onVideoUpdate);
    }
  }, [USE_MEDIAMTX]);

  // ✅ Updated handlers to refresh status after actions
  const onOpenAudienceForStart = () => setAudienceOpen(true);
  const onPause = () => setBroadcastState('paused');
  const onResume = () => setBroadcastState('live');
  const onStop = () => { 
    setBroadcastState('stopped'); 
    setDuration('00:00:00'); 
    setAudienceOpen(false);
    
    // ✅ Refresh status after stopping
    if (USE_MEDIAMTX) {
      setTimeout(() => fetchMediaMTXStatus(), 1000);
    }
  };

  const handleStartBroadcastFromDrawer = (keys: string[]) => {
    setAudienceKeys(keys);
    setBroadcastState('live');
    setDuration('00:00:00');
    setAudienceOpen(false);
    
    // ✅ Refresh status after starting
    if (USE_MEDIAMTX) {
      setTimeout(() => fetchMediaMTXStatus(), 2000);
    }
  };

  const handleUpdateAudienceFromDrawer = (keys: string[]) => {
    setAudienceKeys(keys);
    setAudienceOpen(false);
    
    if (USE_MEDIAMTX) {
      setTimeout(() => fetchMediaMTXStatus(), 1000);
    }
  };

  const handleMuteToggle = (streamId: string) => {
    if (USE_MEDIAMTX) {
      mediamtxService.setMicMuted(streamId, true);
    } else {
      const stream = streams.find(s => s.id === streamId);
      const currentlyMuted = !stream?.isAudioOn;
      mediasoupService.setMicMuted(streamId, !currentlyMuted);
    }
  };

  const handleRemoveHost = async () => {
    if (!hostStream) return;
    
    try {
      if (USE_MEDIAMTX) {
        mediamtxService.removeHost(hostStream.deviceName);
        mediamtxService.setMicMuted(hostStream.deviceName, true);

        const devices = BroadcastStorage.getSelectedDevices();
        const streamId = BroadcastStorage.getStreamId();
        const hasActiveBroadcast = devices.length > 0 && streamId && (broadcastState === 'live' || broadcastState === 'paused');
        
        if (hasActiveBroadcast) {
          const { hostId, coHostId } = mediamtxService.getCurrentRoles();
          
          const payload = {
            streamName: BroadcastStorage.getBroadcastName(),
            devices: devices.map((s) => ({
              deviceSerial: s,
              isHost: s === hostId,
              isCoHost: s === coHostId,
              action: 'update' as const,
            })),
            newHostDeviceSerial: hostId || undefined,
          };

          await MediaMTXUpdateBroadcast(streamId, payload);
          message.success('Host removed');
          
          // ✅ Refresh status after host removal
          setTimeout(() => fetchMediaMTXStatus(), 1000);
        } else {
          message.success('Host removed');
        }
      } else {
        // MediaSoup logic unchanged...
        mediasoupService.removeHost(hostStream.id);
        // ... existing MediaSoup update logic ...
      }
      
    } catch (error: any) {
      console.error('Failed to remove host:', error);
      message.error(error?.message || 'Failed to remove host');
    }
  };

  const handleRemoveCoHost = async () => {
    if (!coHostStream) return;
    
    try {
      if (USE_MEDIAMTX) {
        // ✅ STEP 1: Remove co-host role first
        mediamtxService.removeCoHost(coHostStream.deviceName);

        // ✅ STEP 2: Wait for role removal to complete
        await new Promise<void>((resolve) => {
          const checkRoles = () => {
            const { coHostId } = mediamtxService.getCurrentRoles();
            if (!coHostId) {
              mediamtxService.off('rolesChanged', checkRoles);
              resolve();
            }
          };
          
          mediamtxService.on('rolesChanged', checkRoles);
          setTimeout(() => {
            mediamtxService.off('rolesChanged', checkRoles);
            resolve();
          }, 5000);
        });

        // ✅ STEP 3: Then mute them after role removal is confirmed
        mediamtxService.setMicMuted(coHostStream.deviceName, true);

        const devices = BroadcastStorage.getSelectedDevices();
        const streamId = BroadcastStorage.getStreamId();
        const hasActiveBroadcast = devices.length > 0 && streamId && (broadcastState === 'live' || broadcastState === 'paused');
        
        if (hasActiveBroadcast) {
          const { hostId, coHostId } = mediamtxService.getCurrentRoles();
          const payload = {
            streamName: BroadcastStorage.getBroadcastName(),
            devices: devices.map((s) => ({
              deviceSerial: s,
              isHost: s === hostId,
              isCoHost: s === coHostId,
              action: 'update' as const,
            })),
            newHostDeviceSerial: hostId || undefined,
          };

          await MediaMTXUpdateBroadcast(streamId, payload);
          message.success('Co-host removed');
          
          // ✅ Refresh status after co-host removal
          setTimeout(() => fetchMediaMTXStatus(), 1000);
        } else {
          message.success('Co-host removed');
        }
      } else {
        // MediaSoup logic unchanged...
        mediasoupService.removeCoHost(coHostStream.id);
        // ... existing MediaSoup update logic ...
      }
      
    } catch (error: any) {
      console.error('Failed to remove co-host:', error);
      message.error(error?.message || 'Failed to remove co-host');
    }
  };

  const handleSwitchRoles = async () => {
    const service = USE_MEDIAMTX ? mediamtxService : mediasoupService;
    const { hostId, coHostId } = service.getCurrentRoles();

    if (!hostId || !coHostId) {
      return message.warning('Need both Host and Co-Host to swap');
    }

    if (USE_MEDIAMTX) {
      await assignHostAndCoHost(coHostId, hostId);
      
      const devices = BroadcastStorage.getSelectedDevices();
      const streamId = BroadcastStorage.getStreamId();
      if (devices.length && streamId) {
        const payload = {
          streamName: BroadcastStorage.getBroadcastName(),
          devices: devices.map((s) => ({
            deviceSerial: s,
            isHost: s === coHostId, // New host
            isCoHost: s === hostId,  // New co-host
            action: 'update' as const,
          })),
          newHostDeviceSerial: coHostId,
        };
        try {
          await MediaMTXUpdateBroadcast(streamId, payload);
          message.success('Roles switched and broadcast updated');
          
          setTimeout(() => fetchMediaMTXStatus(), 1000);
        } catch (e) {
          message.error('Failed to update broadcast after switching roles');
        }
      }
    } else {
      service.swapRoles(hostId, coHostId);
      message.success('Switching Host & Co-Host...');
    }
  };

  const handlePromoteToHost = async (streamId: string) => {
    if (USE_MEDIAMTX) {
      const stream = streams.find(s => s.id === streamId);
      const deviceName = stream?.deviceName || streamId;
      mediamtxService.setHost(deviceName);

      // --- Update broadcast ---
      const devices = BroadcastStorage.getSelectedDevices();
      const streamIdNum = BroadcastStorage.getStreamId();
      if (devices.length && streamIdNum) {
        const { coHostId } = mediamtxService.getCurrentRoles();
        const payload = {
          streamName: BroadcastStorage.getBroadcastName(),
          devices: devices.map((s) => ({
            deviceSerial: s,
            isHost: s === deviceName,
            isCoHost: s === coHostId,
            action: 'update' as const,
          })),
          newHostDeviceSerial: deviceName,
        };
        try {
          await MediaMTXUpdateBroadcast(streamIdNum, payload);
          message.success('Host promoted and broadcast updated');
          
          // ✅ Refresh status after promoting host
          setTimeout(() => fetchMediaMTXStatus(), 1000);
        } catch (e) {
          message.error('Failed to update broadcast after promoting host');
        }
      }
    } else {
      mediasoupService.setHost(streamId);
    }
  };

  const hostStream = streams.find(s => s.isHost === true);
  const coHostStream = streams.find(s => s.isHost === false);
  const shouldShowLiveStreams = hostStream || coHostStream || broadcastState !== 'stopped';
  const hostCoHostIds = [
    ...(hostStream ? [hostStream.id] : []),
    ...(coHostStream ? [coHostStream.id] : []),
  ];
  const shouldHideFromGrid = hostCoHostIds.length > 0;

  const EmptyTile: React.FC<{ label: 'Host' | 'Co-Host' }> = ({ label }) => (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Icon name={label === 'Host' ? 'Crown' : 'UserPlus'} size={16} className={label === 'Host' ? 'text-primary' : 'text-muted-foreground'} />
        <span className={`text-sm font-medium ${label === 'Host' ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      </div>
      <div className="aspect-video bg-muted border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center">
        <Icon name={label === 'Host' ? 'Crown' : 'UserPlus'} size={32} className="text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-3">No {label.toLowerCase()} assigned</p>
      </div>
    </div>
  );

  const [muteStates, setMuteStates] = useState<Map<string, { selfMuted: boolean; adminMuted: boolean }>>(new Map());

  useEffect(() => {
    const service = USE_MEDIAMTX ? mediamtxService : mediasoupService;
    const onStreamStatusUpdate = (data: any) => {
      if (data.muteStates) {
        setMuteStates(new Map(Object.entries(data.muteStates)));
      }
    };

    const onMuteUpdate = (data: { id: string; muteState: any }) => {
      setMuteStates(prev => {
        const next = new Map(prev);
        next.set(data.id, {
          selfMuted: data.muteState?.selfMuted ?? false,
          adminMuted: data.muteState?.adminMuted ?? false
        });
        return next;
      });
    };

    service.on('streamStatusUpdate', onStreamStatusUpdate);
    service.on('mute', onMuteUpdate);

    return () => {
      service.off('streamStatusUpdate', onStreamStatusUpdate);
      service.off('mute', onMuteUpdate);
    };
  }, [USE_MEDIAMTX]);

  const hasHost = !!hostStream;
  const hasCoHost = !!coHostStream;

  return (
    <Layout style={{ 
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      transition: 'background-color 0.3s ease'
    }}>
      <Header title="StreamCast Manager" />
      <Layout>
        {/* Left stats panel */}
        <Sider width={300} collapsed={isStatsCollapsed} collapsedWidth={0} trigger={null}
            style={{ 
              backgroundColor: 'var(--color-muted)', 
              borderRight: '1px solid var(--color-border)',
              transition: 'background-color 0.3s ease, border-color 0.3s ease'
            }}>
          <div style={{ padding: 16 }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="BarChart" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Quick Stats</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setIsStatsCollapsed(true)}>
                <Icon name="ChevronLeft" size={16} />
              </Button>
            </div>
            <div className="space-y-3">
              {/* <ParticipantCounter /> */}
              <StreamQualityIndicator />
            </div>
          </div>
        </Sider>

        <Content>
          <div>
            <div className="space-y-6">
              {shouldShowLiveStreams && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-lg font-medium text-foreground">
                        {/* ✅ Dynamic title based on state */}
                        {broadcastState === 'stopped' 
                          ? (hostStream || coHostStream ? 'Live Streams' : 'Live Streams') 
                          : 'Active Broadcast'
                        }
                      </h2>
                      
                      {/* ✅ Show broadcast info only when active */}
                      {broadcastState !== 'stopped' && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-lg">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium text-primary">
                            Broadcast Name - {broadcastName || ''}
                          </span>
                        </div>
                      )}

                      {/* ✅ Show offline indicator when broadcast stopped but roles assigned */}
                      {broadcastState === 'stopped' && (hostStream || coHostStream) && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-muted border border-border rounded-lg">
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <span className="text-sm font-medium text-muted-foreground">
                            Broadcast Offline
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* ✅ Audience button only when broadcast is active */}
                      {broadcastState !== 'stopped' && (
                        <Button variant="outline" size="sm" onClick={() => setAudienceOpen(true)} iconName="Users">
                          Audience
                        </Button>
                      )}
                      <BroadcastControls
                        state={broadcastState}
                        duration={duration}
                        onStart={onOpenAudienceForStart}
                        onPause={onPause}
                        onResume={onResume}
                        onStop={onStop}
                        onSwitchRoles={handleSwitchRoles}
                        hasHost={hasHost}
                        hasCoHost={hasCoHost}
                      />
                    </div>
                  </div>

                  {/* ✅ Always show the grid layout when panel is visible */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Host Section */
                    hostStream ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Icon name="Crown" size={16} className="text-primary" />
                            <span className="text-sm font-medium text-foreground">Host</span>
                          </div>
                          {/* ✅ FIXED: Hide Remove Host button when broadcast is active */}
                          {broadcastState === 'stopped' && (
                            <Button
                              type="default"
                              danger
                              size="sm"
                              onClick={handleRemoveHost}
                              disabled={!!coHostStream}
                            >
                              Remove Host
                            </Button>
                          )}
                        </div>
                        <HostVideoStream
                          stream={hostStream}
                          isHost={true}
                          onMuteToggle={handleMuteToggle}
                        />
                      </div>
                    ) : (
                      // Show empty tile only when broadcast is active
                      broadcastState !== 'stopped' && <EmptyTile label="Host" />
                    )}

                    {/* Co-Host Section */}
                    {coHostStream ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Icon name="UserCheck" size={16} className="text-accent" />
                            <span className="text-sm font-medium text-foreground">Co-Host</span>
                          </div>
                          {/* ✅ FIXED: Hide Remove Co-Host button when broadcast is active */}
                          
                            <Button
                              type="default"
                              danger
                              size="sm"
                              onClick={handleRemoveCoHost}
                            >
                              Remove Co-Host
                            </Button>
                        </div>
                        <HostVideoStream
                          stream={coHostStream}
                          isHost={false}
                          onMuteToggle={handleMuteToggle}
                          onRoleChange={handlePromoteToHost}
                        />
                      </div>
                    ) : (
                      // Show empty tile only when broadcast is active
                      broadcastState !== 'stopped' && <EmptyTile label="Co-Host" />
                    )}
                  </div>

                  {/* ✅ Different status messages based on state */}
                  {broadcastState === 'stopped' && (hostStream || coHostStream) && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-800 font-medium mb-1">
                            Broadcast Offline - Roles Assigned
                          </p>
                          <p className="text-sm text-yellow-700">
                            {hostStream && coHostStream ? 'Host and Co-Host are assigned' : 
                             hostStream ? 'Host is assigned' : 'Co-Host is assigned'} 
                            , but the broadcast isn't started. Start a new broadcast to go live.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {broadcastState !== 'stopped' && !hostStream && !coHostStream && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm text-blue-800 font-medium mb-1">
                            Broadcast Active - Assign Host & Co-Host Roles
                          </p>
                          <p className="text-sm text-blue-700">
                            Your broadcast is live. 
                            Select devices from the grid below to assign Host and Co-Host roles for better stream management.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {broadcastState !== 'stopped' && (hostStream || coHostStream) && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-green-800">
                        <Icon name="CheckCircle" size={14} className="text-green-600" />
                        <span>
                          {hostStream && coHostStream ? 'Host and Co-Host assigned' : 
                           hostStream ? 'Host assigned' : 'Co-Host assigned'} 
                          • Broadcast running smoothly
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Streams Grid */}
              <div className="bg-card border border-border rounded-lg p-6">
                <StreamManagementGrid excludeIds={hostCoHostIds} />
              </div>
            </div>
          </div>
        </Content>

        {/* Right raised-hands side panel */}
        <Sider width={320} collapsed={isHandsPanelCollapsed} collapsedWidth={0} trigger={null}
               style={{ 
                 backgroundColor: 'var(--color-muted)', 
                 borderLeft: '1px solid var(--color-border)',
                 transition: 'background-color 0.3s ease, border-color 0.3s ease'
               }}>
          <RaiseHandsPanel
            isCollapsed={isHandsPanelCollapsed}
            onToggleCollapse={setIsHandsPanelCollapsed}
            onCountChange={setRaisedHandsCount} 
          />
        </Sider>
      </Layout>

      {/* Floating toggles */}
      {/* {isStatsCollapsed && (
        <div style={{ position: 'fixed', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 1000 }}>
          <Button variant="primary" onClick={() => setIsStatsCollapsed(false)}
                  style={{ width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="BarChart" size={18} />
          </Button>
        </div>
      )} */}
      {isHandsPanelCollapsed && (
        <div style={{ position: 'fixed', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 1000 }}>
            <Button variant="primary" onClick={() => setIsHandsPanelCollapsed(false)}
                    style={{ width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="Hand" size={20} />
            </Button>
          {/*   Count badge */}
            {raisedHandsCount > 0 && (
              <div 
                style={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: '#ef4444', // red-500
                  color: 'white',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {raisedHandsCount}
              </div>
            )}
        </div>
      )}

      {/* Audience Drawer */}
      <AudienceSelectorDrawer
        open={audienceOpen}
        state={broadcastState}
        selectedKeys={audienceKeys}
        broadcastName={broadcastName}
        onClose={() => setAudienceOpen(false)}
        onStartBroadcast={handleStartBroadcastFromDrawer}
        onUpdateAudience={handleUpdateAudienceFromDrawer}
      />
    </Layout>
  );
};

export default MainStreamingDashboard;
